/**
 * Scheduling service (Phase 9A). Component → ScheduleService → apiClient.
 *
 * Errors are thrown (never turned into empty lists), so a failed load shows an error instead of
 * an empty calendar. A 409 WARNINGS_NOT_ACKNOWLEDGED carries `details.warnings`.
 */
import { apiClient } from './api-client';
import { ApiError, isApiError } from '../dtos/common/api.dto';
import { fetchUsers } from './admin/user.service';
import type { UserData } from '../dtos/admin/user.dto';
import type {
  BulkAction, BulkResult, CancelCode, CoverageWeek, CreateShiftRequest, DryRunResult, EditScope,
  ScheduleWarning, Series, Shift, ShiftFilters, ShiftListResult, ShiftWriteResult, TodayBoard,
  UpdateShiftRequest,
} from '../dtos/scheduling/schedule.dto';

function unwrap<T>(response: { success: boolean; data?: T; message?: string }, fallback: string): T {
  if (response.success && response.data !== undefined) return response.data;
  throw new Error(response.message || fallback);
}

/** The warnings a 409 WARNINGS_NOT_ACKNOWLEDGED returned, or null for any other error. */
export function warningsFromError(error: unknown): ScheduleWarning[] | null {
  if (isApiError(error) && error.domainCode === 'WARNINGS_NOT_ACKNOWLEDGED') {
    const warnings = (error.details as { warnings?: ScheduleWarning[] } | undefined)?.warnings;
    return Array.isArray(warnings) ? warnings : [];
  }
  return null;
}

/** A readable message for a schedule API error (uses the server message for 4xx). */
export function scheduleErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500 && error.message) return error.message;
  return fallback;
}

export class ScheduleService {
  static async list(filters: ShiftFilters): Promise<Shift[]> {
    const response = await apiClient.get<ShiftListResult>('/schedule/daterange', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      providerId: filters.providerId,
      patientId: filters.patientId,
      discipline: filters.discipline,
      status: filters.status,
      visitType: filters.visitType,
      unstaffed: filters.unstaffed ? '1' : undefined,
    });
    return unwrap(response, 'Failed to load the schedule').items || [];
  }

  static async listByPatient(patientId: string, startDate?: string, endDate?: string): Promise<Shift[]> {
    const response = await apiClient.get<ShiftListResult>(`/schedule/patient/${encodeURIComponent(patientId)}`, { startDate, endDate });
    return unwrap(response, 'Failed to load the schedule').items || [];
  }

  /** "My schedule" (SCH-08). providerId 'me' is the signed-in clinician. */
  static async listByProvider(providerId: string, startDate?: string, endDate?: string): Promise<Shift[]> {
    const response = await apiClient.get<ShiftListResult>(`/schedule/provider/${encodeURIComponent(providerId)}`, { startDate, endDate });
    return unwrap(response, 'Failed to load the schedule').items || [];
  }

  static async get(id: string): Promise<Shift & { series: Series | null }> {
    const response = await apiClient.get<Shift & { series: Series | null }>(`/schedule/${encodeURIComponent(id)}`);
    return unwrap(response, 'Failed to load the shift');
  }

  static async preview(request: CreateShiftRequest): Promise<DryRunResult> {
    const response = await apiClient.post<DryRunResult>('/schedule?dryRun=1', request);
    return unwrap(response, 'Failed to check the schedule');
  }

  static async create(request: CreateShiftRequest): Promise<ShiftWriteResult> {
    const response = await apiClient.post<ShiftWriteResult>('/schedule', request);
    return unwrap(response, 'Failed to create the shift');
  }

  static async previewUpdate(id: string, updates: UpdateShiftRequest, scope: EditScope = 'this'): Promise<DryRunResult> {
    const response = await apiClient.put<DryRunResult>(`/schedule/${encodeURIComponent(id)}?dryRun=1&scope=${scope}`, updates);
    return unwrap(response, 'Failed to check the change');
  }

  static async update(id: string, updates: UpdateShiftRequest, scope: EditScope = 'this'): Promise<ShiftWriteResult> {
    const response = await apiClient.put<ShiftWriteResult>(`/schedule/${encodeURIComponent(id)}?scope=${scope}`, updates);
    return unwrap(response, 'Failed to update the shift');
  }

  static async cancel(id: string, code: CancelCode, note?: string, scope: 'this' | 'following' = 'this'): Promise<Shift[]> {
    const response = await apiClient.post<{ shifts: Shift[] }>(`/schedule/${encodeURIComponent(id)}/cancel`, { code, note, scope });
    return unwrap(response, 'Failed to cancel the shift').shifts || [];
  }

  /** Allowed only for a shift you created in the last 24 hours; otherwise cancel it. */
  static async remove(id: string): Promise<void> {
    unwrap(await apiClient.delete<{ deleted: boolean }>(`/schedule/${encodeURIComponent(id)}`), 'Failed to delete the shift');
  }

  static async bulk(action: BulkAction, body: Record<string, unknown>, { dryRun = false } = {}): Promise<BulkResult> {
    const response = await apiClient.post<BulkResult>(`/schedule/bulk${dryRun ? '?dryRun=1' : ''}`, { action, ...body });
    return unwrap(response, 'The bulk action failed');
  }

  static async today(date?: string): Promise<TodayBoard> {
    const response = await apiClient.get<TodayBoard>('/schedule/today', { date });
    return unwrap(response, 'Failed to load today\'s board');
  }

  static async coverage(weekStart: string, patientIds?: string[]): Promise<CoverageWeek> {
    const response = await apiClient.get<CoverageWeek>('/schedule/coverage', {
      weekStart,
      patientIds: patientIds && patientIds.length ? patientIds.join(',') : undefined,
    });
    return unwrap(response, 'Failed to load coverage');
  }

  /** Active clinicians for the pickers (provider type kept as stored: RN and LVN stay distinct). */
  static async listClinicians(): Promise<UserData[]> {
    const { users } = await fetchUsers(1, 500, undefined, 'ACTIVE');
    return users.filter((u) => u.isProvider && u.providerType && u.providerType !== 'not_a_provider' && u.providerType !== 'patientFamily');
  }
}

/** @deprecated pre-9A name, kept for patientLifecycle.service. */
export const listScheduleEntriesByPatient = async (patientId: string, options: { startDate?: string; endDate?: string } = {}) => ({
  items: await ScheduleService.listByPatient(patientId, options.startDate, options.endDate),
});

export type ScheduleEntry = Shift;
